import clsx from 'clsx';
import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { stringToCapitalized } from '@vestico/utils';

import { ProductVariant, getProductVariantsBySize } from '@vestico/model';

import { Button, Dropdown, DropdownOption, GlobalsContext, Modal, ModalSizing, UserContext } from '@vestico/shared';
import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { MatchingAPIContext, ProductContext, WidgetAuthContext } from '../../MainContext';
import { ConfigContext } from '../../WidgetEventsManager';
import { WidgetRootElementContext } from '../../components/MainWindow';
import style from './SizeRecommendationFineTune.module.css';

export interface SizeRecommendationFineTuneProps {
  additionalProductAttributes: string[] | undefined;
}

export const SizeRecommendationFineTune: FunctionComponent<SizeRecommendationFineTuneProps> = ({ additionalProductAttributes }) => {
  const { isFineTune } = useContext(ConfigContext);
  const { useMobileLayout } = useContext(GlobalsContext);
  const { product } = useContext(ProductContext);
  const rootElement = useContext(WidgetRootElementContext);
  const [open, setOpen] = useState(false);

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(undefined);
  const [selectedVariantSizeIndex, setSelectedVariantSizeIndex] = useState<number | undefined>(undefined);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const api = useContext(MatchingAPIContext);
  const auth = useContext(WidgetAuthContext);
  const { user } = useContext(UserContext);

  const variantsBySize = useMemo(() => {
    if (!product || !isFineTune) {
      return undefined;
    }

    return getProductVariantsBySize(product, true);
  }, [isFineTune, product]);

  const sizes = useMemo(() => {
    if (!variantsBySize) {
      return undefined;
    }
    return [...variantsBySize.keys()];
  }, [variantsBySize]);

  const toggleFineTuneModal = useCallback(() => {
    if (!isFineTune) {
      return;
    }
    setOpen(!open);
  }, [isFineTune, open]);

  const variantsForSize = useMemo(
    () =>
      selectedVariantSizeIndex !== undefined && variantsBySize && sizes && isFineTune
        ? variantsBySize.get(sizes[selectedVariantSizeIndex])
        : undefined,
    [isFineTune, selectedVariantSizeIndex, sizes, variantsBySize]
  );

  const [selectedAdditionalAttributeIndexes, setSelectedAdditionalAttributeIndexes] = useState<(number | undefined)[]>(
    new Array(additionalProductAttributes?.length ?? 0)
  );

  const availableAttributeValuesPerAttribute = useMemo(() => {
    return variantsForSize && additionalProductAttributes
      ? additionalProductAttributes.map((attribute) => {
          const attributeValues = variantsForSize
            .map((v) => {
              return v.otherOptions[attribute] ?? v.otherOptions[stringToCapitalized(attribute)];
            })
            .filter((v) => !!v);
          return attributeValues;
        })
      : null;
  }, [additionalProductAttributes, variantsForSize]);

  const updateSelectedVariant = useCallback(() => {
    if (!sizes || !variantsBySize || !variantsForSize || selectedVariantSizeIndex === undefined) {
      return;
    }

    const finalVariants = variantsForSize.filter((sizeVariant) => {
      if (additionalProductAttributes && availableAttributeValuesPerAttribute) {
        for (let index = 0; index < additionalProductAttributes.length; index++) {
          const additionalAttributeName = additionalProductAttributes[index];
          const selectedAdditionalAttributeIndex = selectedAdditionalAttributeIndexes[index];
          if (selectedAdditionalAttributeIndex !== undefined) {
            const valueName =
              sizeVariant.otherOptions[additionalAttributeName] ?? sizeVariant.otherOptions[stringToCapitalized(additionalAttributeName)];
            const availableAttributeValue = availableAttributeValuesPerAttribute[index][selectedAdditionalAttributeIndex];
            // Exclude variants if a value has been selected but they don't match
            if (valueName?.toLowerCase() !== availableAttributeValue?.toLowerCase()) {
              return false;
            }
          }
        }
      }

      return true;
    });

    setSelectedVariant(finalVariants[0]);
  }, [
    additionalProductAttributes,
    availableAttributeValuesPerAttribute,
    selectedAdditionalAttributeIndexes,
    selectedVariantSizeIndex,
    sizes,
    variantsBySize,
    variantsForSize,
  ]);

  useEffect(() => {
    updateSelectedVariant();
  }, [updateSelectedVariant]);

  const didUpdateSelectedOption = useCallback((optionIndex: number) => {
    setSelectedVariantSizeIndex(optionIndex);
  }, []);

  const onSendFineTune = useCallback(async () => {
    const userUID = user?.uid;
    if (requestInProgress || !api || !selectedVariant || !product || !userUID || !isFineTune) {
      return;
    }

    setRequestInProgress(true);

    const [success, error] = await api.recordSizeRecFineTune({ product, selectedVariantUID: selectedVariant.uid, user, authRequestInfo: auth });
    if (success) {
      toggleFineTuneModal();
    } else {
      setError(error?.message ?? 'Unknown error');
    }
    setRequestInProgress(false);
  }, [api, auth, isFineTune, product, requestInProgress, selectedVariant, toggleFineTuneModal, user]);

  const onSelectUpdateAdditionAttribute = useCallback(
    (attributeIndex: number) => (selectedIndex: number) => {
      selectedAdditionalAttributeIndexes[attributeIndex] = selectedIndex;
      setSelectedAdditionalAttributeIndexes([...selectedAdditionalAttributeIndexes]);
    },
    [selectedAdditionalAttributeIndexes]
  );

  const modal = useMemo(() => {
    if (!isFineTune || !sizes) {
      return null;
    }

    const options = sizes.map((size) => {
      const dropDownOption: DropdownOption = {
        content: size,
      };
      return dropDownOption;
    });

    const additionalDropdowns = availableAttributeValuesPerAttribute
      ? availableAttributeValuesPerAttribute.map((attributes, index) => {
          const attributeValues = attributes.map((v) => {
            return { content: v };
          });

          if (attributeValues.length === 0) {
            return null;
          }

          return (
            <Dropdown
              key={attributes.join('-')}
              options={attributeValues}
              didUpdateOption={onSelectUpdateAdditionAttribute(index)}
              selectedOptionIndex={selectedAdditionalAttributeIndexes[index]}
              defaultSelectedOption="Please select"
              size="small"
            />
          );
        })
      : undefined;

    return (
      <Modal open={open} sizing={ModalSizing.intrinsicHeight} alignment={useMobileLayout ? 'bottom' : 'center'} header="Fine-tune recommendation">
        <div className={clsx(style.root)}>
          <div className={clsx(style.sizeAdjust)}>
            <span>My size should be: </span>
            <div className={clsx(style.dropdowns)}>
              <Dropdown
                options={options}
                didUpdateOption={didUpdateSelectedOption}
                selectedOptionIndex={selectedVariantSizeIndex}
                defaultSelectedOption="Please select"
                size="small"
              />
              {additionalDropdowns}
            </div>
          </div>
          <div className={clsx(style.buttons)}>
            <div>
              <Button content="Cancel" onClick={toggleFineTuneModal} outline />
            </div>
            <div>
              <div className={clsx(globalStyle.textSmall, globalStyle.textColorError)}>{error}</div>
              <Button content="Submit" onClick={onSendFineTune} disabled={!selectedVariant || requestInProgress} loading={requestInProgress} />
            </div>
          </div>
        </div>
      </Modal>
    );
  }, [
    isFineTune,
    sizes,
    availableAttributeValuesPerAttribute,
    open,
    useMobileLayout,
    didUpdateSelectedOption,
    selectedVariantSizeIndex,
    toggleFineTuneModal,
    error,
    onSendFineTune,
    selectedVariant,
    requestInProgress,
    onSelectUpdateAdditionAttribute,
    selectedAdditionalAttributeIndexes,
  ]);

  const tuneButton = useMemo(
    () =>
      isFineTune ? (
        <div className={clsx(style.btnWrapper)}>
          <Button content="Fine-tune recommendation" onClick={toggleFineTuneModal} outline />
        </div>
      ) : null,
    [isFineTune, toggleFineTuneModal]
  );

  const portal = modal ? createPortal(modal, rootElement ?? document.body) : null;

  return (
    <div>
      {tuneButton}
      {portal}
    </div>
  );
};
